// SPDX-License-Identifier: AGPL-3.0-or-later
// SPDX-FileCopyrightText: 2026 Ben Richardson <hi@ben.gy>
import type { Ctx } from '../main.ts';
import { GROUP_META, GROUP_ORDER, partyColor, type Group } from '../parties.ts';
import { STATE_NAMES } from '../data.ts';
import { fmtInt, fmtPct, fmtSigned, esc, escTip } from '../utils/format.ts';
import { term } from '../glossary.ts';

const STATES = ['NSW', 'VIC', 'QLD', 'WA', 'SA', 'TAS', 'ACT', 'NT'];

function mix(hex: string, t: number): string {
  const c = [parseInt(hex.slice(1, 3), 16), parseInt(hex.slice(3, 5), 16), parseInt(hex.slice(5, 7), 16)];
  const w = [255, 255, 255];
  const o = c.map((v, i) => Math.round(w[i] + (v - w[i]) * t));
  return `rgb(${o[0]},${o[1]},${o[2]})`;
}

export function renderFirstPrefs(root: HTMLElement, ctx: Ctx): void {
  const parties = ctx.data.parties.filter((p) => p.pct >= 0.15);
  const maxPct = Math.max(...parties.map((p) => p.pct));
  const majorPrimary = ctx.data.parties.filter((p) => p.ab === 'ALP' || p.group === 'COA').reduce((a, p) => a + p.pct, 0);

  const sfp = ctx.data.stateFirstPrefs;
  const maxCell = Math.max(...sfp.flatMap((s) => GROUP_ORDER.map((g) => s.groups[g] || 0)));

  root.innerHTML = `
    <div class="view-head">
      <h2>First preferences</h2>
      <p class="subtitle">The primary vote — who voters put first, before any ${term('preferences')} are distributed. In 2025 the major parties together took just ${fmtPct(majorPrimary)}, the lowest on record.</p>
    </div>

    <div class="panel">
      <div class="panel-title">National first-preference vote</div>
      <div class="panel-sub">Share of all formal ${term('firstpref', 'first-preference')} votes, with the change since 2022.</div>
      <div id="np-bars"></div>
    </div>

    <div class="panel">
      <div class="panel-title">Where each side is strong — first preferences by state</div>
      <div class="panel-sub">Each cell is a bloc's primary vote in that state. Darker means stronger. This is why the same national result looks so different across the country.</div>
      <div class="table-scroll">
        <table class="data matrix">
          <thead><tr><th>Bloc</th>${STATES.map((s) => `<th class="num" title="${esc(STATE_NAMES[s])}">${s}</th>`).join('')}</tr></thead>
          <tbody>
            ${GROUP_ORDER.map((g) => `
              <tr>
                <td><span class="pill" style="background:${GROUP_META[g].color}"><span class="dot"></span>${GROUP_META[g].label}</span></td>
                ${STATES.map((st) => {
                  const v = (sfp.find((x) => x.state === st)?.groups[g] as number) || 0;
                  return `<td class="num cell" style="background:${mix(GROUP_META[g].color, v / maxCell)};color:${v / maxCell > 0.55 ? '#fff' : 'var(--text-primary)'}" data-tip="${escTip(`${GROUP_META[g].label} in ${STATE_NAMES[st]}\n${fmtPct(v)} of first preferences`)}">${v ? v.toFixed(1) : '·'}</td>`;
                }).join('')}
              </tr>`).join('')}
          </tbody>
        </table>
      </div>
      <p class="note">Blocs group related parties: Coalition combines Liberal, the Queensland LNP, the Nationals and the NT Country Liberals; “Other” is every remaining party (One Nation, Trumpet of Patriots, Family First and so on).</p>
    </div>`;

  const bars = root.querySelector('#np-bars')!;
  bars.innerHTML = parties.map((p) => {
    const color = partyColor(p.ab, p.group as Group);
    const swClass = p.swing > 0.05 ? 'var(--status-good)' : p.swing < -0.05 ? 'var(--status-bad)' : 'var(--text-tertiary)';
    return `<div class="barrow">
      <span class="lab" title="${esc(p.name)}">${esc(p.name)}${p.seats ? ` <span style="color:var(--text-tertiary);font-weight:400">· ${p.seats} seat${p.seats === 1 ? '' : 's'}</span>` : ''}</span>
      <span class="track"><span class="fill" style="width:${(p.pct / maxPct) * 100}%;background:${color}" data-tip="${escTip(`${p.name}\n${fmtPct(p.pct)} · ${fmtInt(p.votes)} votes\nSwing ${fmtSigned(p.swing)} · ${p.seats} seat${p.seats === 1 ? '' : 's'}`)}"></span></span>
      <span class="val">${fmtPct(p.pct)} <span style="color:${swClass};font-size:var(--fs-xs)">${fmtSigned(p.swing)}</span></span>
    </div>`;
  }).join('');
}
